import React, { useEffect, useState } from 'react';
import api from '../../lib/api';
import { FormField } from './FormField';

const VIDEO_RE = /\.(mp4|mov|webm)(\?|$)/i;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const isEmpty = (v) => v == null || v === '' || v === false || (Array.isArray(v) && v.length === 0);

// Keep the ORIGINAL section index so ids match the rendered fields (hidden sections must not shift indexes)
const visibleFields = (def) => def.sections.flatMap((s, si) => (s.hidden ? [] :
  s.fields.map((f, fi) => ({ f, s, si, id: `${def.type}-${si}-${fi}` }))));

const SHARED_ROLES = ['name', 'email', 'phone', 'date', 'location'];
const shareKey = (f) => f.share || (SHARED_ROLES.includes(f.role) ? f.role : null);

// Copy answers common to both questionnaires (contact details, date, place, project) when switching universe
export const carryShared = (from, fromVals, to, toVals) => {
  const shared = {};
  visibleFields(from).forEach(({ f, id }) => { const k = shareKey(f); if (k && !isEmpty(fromVals[id])) shared[k] = fromVals[id]; });
  const next = { ...toVals };
  visibleFields(to).forEach(({ f, id }) => { const k = shareKey(f); if (k && k in shared) next[id] = shared[k]; });
  return next;
};

const validate = (def, values) => {
  const errors = {};
  visibleFields(def).forEach(({ f, id }) => {
    const v = values[id];
    if (f.required && (typeof v === 'string' ? !v.trim() : isEmpty(v))) {
      errors[id] = f.error || (f.type === 'consent' ? 'Veuillez cocher cette case.' : f.type === 'file' ? 'Veuillez joindre le fichier demandé.' : 'Ce champ est obligatoire.');
    } else if (f.videoOnly && v?.length && !v.some((x) => VIDEO_RE.test(x.url))) {
      errors[id] = 'Veuillez joindre une vidéo (MP4, MOV ou WEBM).';
    } else if (f.input === 'email' && v && !EMAIL_RE.test(v.trim())) {
      errors[id] = 'Veuillez saisir une adresse e-mail valide.';
    }
  });
  return errors;
};

const OTHER_RE = /^autre\b/i;
const otherPicked = (f, v) => (f.type === 'radio' || f.type === 'checkbox') && [].concat(v || []).some((x) => OTHER_RE.test(x));
// Replace "Autre" by "Autre : <précision>" in the submitted answer
const withOther = (v, txt) => {
  if (!txt) return v;
  const fix = (x) => (OTHER_RE.test(x) ? `${x} : ${txt}` : x);
  return Array.isArray(v) ? v.map(fix) : fix(v);
};

const NUM_RE = /^\d{2} — /;
const displayTitles = (def) => {
  let n = 0;
  return def.sections.map((s) => (s.hidden || !NUM_RE.test(s.title) ? s.title : `${String(++n).padStart(2, '0')} — ${s.title.replace(NUM_RE, '')}`));
};

const buildPayload = (def, values, website) => {
  const titles = displayTitles(def);
  const payload = { submissionType: def.type, firstName: '', answers: [], inspirationFiles: [], website, ...(def.eventType ? { eventType: def.eventType } : {}), ...(def.variant ? { formVariant: def.variant } : {}) };
  visibleFields(def).forEach(({ f, si, id }) => {
    if (f.type === 'note') return;
    const v = values[id];
    const section = titles[si];
    if (f.type === 'file') {
      const urls = (v || []).map((x) => x.url);
      payload.inspirationFiles.push(...urls);
      payload.answers.push({ section, label: f.label, key: f.key || f.label, value: urls });
      return;
    }
    const other = otherPicked(f, v) ? (values[`${id}-other`] || '').trim() : '';
    const value = withOther(typeof v === 'string' ? v.trim() : v ?? null, other);
    payload.answers.push({ section, label: f.label, key: f.key || f.label, value });
    if (f.role === 'name') payload.lastName = value;
    else if (f.role && value) payload[f.role] = Array.isArray(value) ? value.join(', ') : value;
  });
  return payload;
};

export const QuestionnaireForm = ({ def, onSuccess, submitLabel = 'Envoyer ma demande', showHeader = true, initialValues, onValuesChange }) => {
  const [values, setValues] = useState(initialValues || {});
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle');
  const [sendErr, setSendErr] = useState('');
  const [website, setWebsite] = useState('');
  const titles = displayTitles(def);
  useEffect(() => { onValuesChange?.(values); }, [values]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (id, v) => {
    setValues((p) => ({ ...p, [id]: v }));
    if (errors[id]) setErrors((p) => ({ ...p, [id]: undefined }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate(def, values);
    setErrors(errs);
    const first = Object.keys(errs)[0];
    if (first) {
      document.querySelector(`[data-testid="field-${first}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      document.querySelector(`[data-testid="field-${first}"] input, [data-testid="field-${first}"] textarea`)?.focus({ preventScroll: true });
      return;
    }
    setStatus('sending');
    try {
      await api.post('/quotes', buildPayload(def, values, website));
      setStatus('done');
      setTimeout(() => document.querySelector('[data-testid="form-success"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50);
      onSuccess?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setSendErr([400, 413, 429].includes(err?.response?.status) && typeof detail === 'string' ? detail : '');
      setStatus('error');
    }
  };

  if (status === 'done') {
    return (
      <div role="status" className="border-t border-[#111111]/15 pt-10" data-testid="form-success">
        <span className="block text-[11px] uppercase tracking-[0.3em] text-[#6B6B6B] mb-5">Merci</span>
        <p className="font-serif font-light text-3xl md:text-4xl text-[#111111] leading-[1.2]">Votre demande nous est bien parvenue.</p>
        <p className="mt-5 text-sm font-light leading-[1.9] text-[#333333] max-w-md">Nous l’étudions avec attention et revenons vers vous très prochainement.</p>
      </div>
    );
  }

  const sections = def.sections.map((s, si) => ({ s, si })).filter(({ s }) => !s.hidden);
  return (
    <form onSubmit={submit} noValidate data-testid={`form-${def.type}`}>
      {showHeader && (
        <div className="mb-14">
          <h2 className="font-serif font-light text-[#111111] text-3xl md:text-4xl leading-[1.15]">{def.title}</h2>
          <p className="mt-5 text-sm font-light leading-[1.9] text-[#333333] whitespace-pre-line">{def.intro}</p>
        </div>
      )}
      <div className="space-y-16">
        {sections.map(({ s, si }) => (
          <section key={s.title} aria-labelledby={`${def.type}-sec-${si}`}>
            <h3 id={`${def.type}-sec-${si}`} className="font-sans text-[11px] uppercase tracking-[0.3em] text-[#111111] border-b border-[#111111]/10 pb-4 mb-10">{titles[si]}</h3>
            <div className="space-y-10">
              {s.fields.map((f, fi) => {
                const id = `${def.type}-${si}-${fi}`;
                return (
                  <div key={id}>
                    <FormField id={id} f={f} value={values[id]} onChange={(v) => set(id, v)} err={errors[id]} />
                    {otherPicked(f, values[id]) && (
                      <input type="text" aria-label={`${f.label} — précisez`} placeholder="Précisez…" maxLength={200}
                        value={values[`${id}-other`] || ''} onChange={(e) => set(`${id}-other`, e.target.value)}
                        className="mt-4 w-full bg-transparent border-0 border-b border-[#111111]/25 px-0 py-3 text-base font-light text-[#111111] placeholder:text-[#6B6B6B]/60 focus:outline-none focus:border-[#111111] transition-colors rounded-none"
                        data-testid={`${id}-other-input`} />
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        ))}
      </div>
      <div aria-hidden="true" className="absolute -left-[9999px] w-px h-px overflow-hidden">
        <label htmlFor={`${def.type}-website`}>Ne pas remplir ce champ</label>
        <input id={`${def.type}-website`} type="text" name="website" tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} />
      </div>
      {status === 'error' && (
        <p role="alert" className="mt-10 text-sm text-[#B42318]" data-testid="form-error">
          {sendErr || 'L’envoi n’a pas pu aboutir. Vos informations sont conservées : veuillez réessayer dans un instant.'}
        </p>
      )}
      {Object.keys(errors).some((k) => errors[k]) && (
        <p role="alert" className="mt-10 text-sm text-[#B42318]" data-testid="form-validation-summary">Certains champs sont à compléter ou à corriger.</p>
      )}
      <button type="submit" disabled={status === 'sending'} data-testid="form-submit"
        className="mt-12 w-full sm:w-auto px-14 py-5 bg-[#111111] text-white text-[11px] uppercase tracking-[0.3em] hover:bg-[#333333] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#111111] disabled:bg-[#6B6B6B] disabled:cursor-not-allowed transition-colors">
        {status === 'sending' ? 'Envoi en cours…' : submitLabel}
      </button>
    </form>
  );
};
